import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Phone, Lock, Save, Wallet } from 'lucide-react';
import api from '../services/api';

function Profile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [profilePassword, setProfilePassword] = useState('');

  const [formData, setFormData] = useState({ full_name: '', email: '', phone_number: '' });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const contactChanged = profile && (
    formData.email !== (profile.email || '') || formData.phone_number !== (profile.phone_number || '')
  );

  const requireNewLogin = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await api.get('/profile');
      setProfile(response.data);
      setFormData({
        full_name: response.data.full_name || '',
        email: response.data.email || '',
        phone_number: response.data.phone_number || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updates = {};
      if (formData.full_name !== profile.full_name) updates.full_name = formData.full_name;
      if (formData.email !== profile.email) updates.email = formData.email;
      if (formData.phone_number !== profile.phone_number) updates.phone_number = formData.phone_number;

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }
      if (contactChanged) {
        if (!profilePassword) {
          toast.error('Enter your current password to change email or phone number.');
          return;
        }
        updates.current_password = profilePassword;
      }

      const response = await api.put('/profile', updates);
      if (response.data.reauthentication_required || contactChanged) {
        toast.success('Contact details updated. Please sign in again.');
        requireNewLogin();
        return;
      }
      setProfile({ ...profile, ...response.data.user });
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfilePassword('');
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await api.post('/profile/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed. Please sign in again.');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      requireNewLogin();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-600">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="app-safe-header bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(profile?.user_type === 'admin' ? '/admin' : '/dashboard')}
            data-testid="profile-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Wallet className="w-6 h-6 text-emerald-900" />
          <h1 className="text-xl font-bold text-emerald-900">Profile & Settings</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Account Info */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
              <User className="w-5 h-5 text-emerald-800" />
              Account Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-6 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
              <div className="w-10 h-10 bg-emerald-800 text-white rounded-full flex items-center justify-center font-bold text-lg">
                {profile?.full_name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-slate-900">{profile?.full_name}</p>
                <p className="text-sm text-emerald-800 capitalize">{profile?.user_type} Account</p>
              </div>
            </div>

            <form onSubmit={handleSaveProfile} className="space-y-4" data-testid="profile-form">
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" /> Full Name
                </Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="input-field"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your@email.com"
                  className="input-field"
                  data-testid="profile-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" /> Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="+27821234567"
                  className="input-field"
                  data-testid="profile-phone-input"
                />
              </div>
              {contactChanged && (
                <div className="space-y-2">
                  <Label htmlFor="profile_current_password">Confirm Current Password</Label>
                  <Input
                    id="profile_current_password"
                    type="password"
                    autoComplete="current-password"
                    value={profilePassword}
                    onChange={e => setProfilePassword(e.target.value)}
                    required
                    data-testid="profile-current-password-input"
                  />
                  <p className="text-sm text-slate-600">
                    Email and phone are used for sign-in and account recovery. Changing them signs you out on all devices.
                  </p>
                </div>
              )}
              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={saving}
                data-testid="profile-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
              <Lock className="w-5 h-5 text-emerald-800" />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4" data-testid="password-form">
              <div className="space-y-2">
                <Label htmlFor="current_password" className="text-sm font-semibold text-slate-700">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  className="input-field"
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password" className="text-sm font-semibold text-slate-700">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  minLength={6}
                  className="input-field"
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password" className="text-sm font-semibold text-slate-700">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                  minLength={6}
                  className="input-field"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={changingPassword}
                data-testid="change-password-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default Profile;
